/* ============================================
   NotaBubble — Nota interna dentro del chat

   Centrada, ni izquierda ni derecha: una nota no es de ninguno de los dos lados
   de la conversacion. Se distingue de un mensaje a simple vista porque si se
   confunde con uno, alguien va a creer que el cliente leyo esto.
   ============================================ */
import { IconFileText, IconEdit, IconTrash, IconPaperclip } from '@/components/icons/Icons'
import type { ConversacionNota, NotaAdjunto } from '@/lib/types'

interface NotaBubbleProps {
  nota: ConversacionNota
  onEdit?: (nota: ConversacionNota) => void
  onDelete?: (nota: ConversacionNota) => void
  onImageClick?: (url: string) => void
}

function formatTime(dateStr: string): string {
  return new Date(dateStr).toLocaleTimeString('es-PE', { hour: '2-digit', minute: '2-digit' })
}

function esImagen(adj: NotaAdjunto): boolean {
  return adj.mime.startsWith('image/')
}

export default function NotaBubble({ nota, onEdit, onDelete, onImageClick }: NotaBubbleProps) {
  const adjuntos = nota.adjuntos || []
  const editada = nota.updated_at && nota.created_at
    && new Date(nota.updated_at).getTime() - new Date(nota.created_at).getTime() > 1000

  return (
    <div className="chat-nota" id={`nota-${nota.id}`}>
      <div className="chat-nota__head">
        <IconFileText size={14} />
        <span className="chat-nota__label">Nota interna</span>
        {nota.autor_nombre && <span className="chat-nota__autor">· {nota.autor_nombre}</span>}
        <span className="chat-nota__hora">{formatTime(nota.fecha)}</span>
        {editada && <span className="chat-nota__editada">editada</span>}

        <div className="chat-nota__acciones">
          {onEdit && (
            <button type="button" className="chat-nota__accion" onClick={() => onEdit(nota)} title="Editar nota">
              <IconEdit size={14} />
            </button>
          )}
          {onDelete && (
            <button type="button" className="chat-nota__accion" onClick={() => onDelete(nota)} title="Eliminar nota">
              <IconTrash size={14} />
            </button>
          )}
        </div>
      </div>

      <div className="chat-nota__cuerpo">{nota.contenido}</div>

      {adjuntos.length > 0 && (
        <div className="chat-nota__adjuntos">
          {adjuntos.map((adj, i) => (
            esImagen(adj) ? (
              <button
                key={i}
                type="button"
                className="chat-nota__thumb"
                onClick={() => onImageClick?.(adj.url)}
                title={adj.nombre}
              >
                <img src={adj.url} alt={adj.nombre} loading="lazy" />
              </button>
            ) : (
              <a key={i} className="chat-nota__archivo" href={adj.url} target="_blank" rel="noreferrer">
                <IconPaperclip size={13} />
                <span>{adj.nombre}</span>
              </a>
            )
          ))}
        </div>
      )}
    </div>
  )
}
